<template>
  <div
    :class="[
      ns.b(),
      ns.is('border', border),
      ns.is('background', background),
      { [ns.m('small')]: small },
      ns.is('checked', isChecked),
      ns.is('disabled', hasType && disabled),
      ns.is('focus', hasType && focus),
      ns.is('has-type', hasType),
      ns.is('has-radio', isRadio),
      ns.is('has-checkbox', isCheckbox),
    ]"
    :style="wrapperStyle"
    @click.stop="handleClick"
    @mouseenter="focus = true"
    @mouseleave="focus = false"
  >
    <div
      v-if="!!_type"
      :class="[ns.e('type'), ns.em('type', isCheckbox ? 'checkbox' : '')]"
    >
      <div
        v-if="isRadio"
        :class="[
          ns.e('type-angle'),
          ns.em('type-angle', isChecked ? 'checked' : ''),
          ns.em('type-angle', disabled ? 'disabled' : ''),
        ]"
      />
      <jd-radio
        v-if="isRadio"
        v-model="modelValue"
        :label="label"
        :class="ns.em('type', 'radio')"
      />
      <jd-checkbox
        v-if="isCheckbox"
        v-model="modelValue"
        :label="label"
        :disabled="disabled"
        size="small"
        @click.stop
        @change="handleCheckboxChange"
      >
        <template #default>{{ '' }}</template>
      </jd-checkbox>
    </div>
    <div :class="[ns.e('main'), ns.em('main', direction)]">
      <div :class="ns.e('value')">
        <div :class="ns.e('head')">
          <div v-if="$slots.title || title" :class="ns.e('title')">
            <slot name="title">
              {{ title }}
            </slot>
          </div>
        </div>
        <div :class="ns.e('content')">
          <div :class="ns.e('value-wrapper')">
            <slot name="content">
              <div v-if="$slots.prefix || prefix" :class="ns.e('prefix')">
                <slot name="prefix">
                  <span>{{ prefix }}</span>
                </slot>
              </div>
              <span :class="ns.e('number')" :style="valueStyle">
                {{ displayValue }}
              </span>
              <div v-if="$slots.suffix || suffix" :class="ns.e('suffix')">
                <slot name="suffix">
                  <span>{{ suffix }}</span>
                </slot>
              </div>
            </slot>
          </div>
          <div :class="ns.e('extra-wrapper')" />
        </div>
        <div
          v-if="$slots.trend || (trend && (trendValue || trendValue === 0))"
          :class="[ns.e('trend'), ns.em('trend', trend)]"
        >
          <slot name="trend">
            <span :class="ns.e('trend-prefix')">{{
              trendPrefix || '同比'
            }}</span>
            <span :class="ns.e('trend-value')">{{ trendValue }}</span>
            <span :class="ns.e('trend-suffix')">{{ trendSuffix }}</span>
          </slot>
        </div>
      </div>
      <div
        v-if="!isVertical && $slots.extra"
        :class="[ns.e('extra'), ns.em('extra', direction)]"
      >
        <slot name="extra" />
      </div>
      <div v-if="$slots.action" :class="ns.e('action')">
        <slot name="action" />
      </div>
    </div>
    <div
      v-if="isVertical && $slots.extra"
      :class="[ns.e('extra'), ns.em('extra', direction)]"
    >
      <slot name="extra" />
    </div>
    <div v-if="$slots.bottom" :class="ns.e('bottom')">
      <jd-divider :margin="8" />
      <div :class="ns.e('bottom-wrapper')" :style="bottomStyle">
        <slot name="bottom" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, inject, ref, unref } from 'vue'
import { useTransition } from '@vueuse/core'
import { isEqual } from 'lodash-unified'
import { UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import { useNamespace } from '@jdesign-vue/hooks'
import { addUnit, isArray, isFunction, isNumber } from '@jdesign-vue/utils'
import { statisticGroupContextKey } from './constants'
import { statisticEmits, statisticProps } from './statistic'

defineOptions({
  name: 'JdStatistic',
})

const props = defineProps(statisticProps)
const emit = defineEmits(statisticEmits)
const ns = useNamespace('statistic')

const focus = ref(false)

const statisticGroupContext = inject(statisticGroupContextKey)

const isGroup = computed(() => !!statisticGroupContext)

const _size = computed(
  () => (statisticGroupContext?.size || props.size) as string
)
const _type = computed(
  () => (statisticGroupContext?.type || props.type) as string
)

const isRadio = computed(() => _type.value === 'radio')
const isCheckbox = computed(() => _type.value === 'checkbox')
const hasType = computed(() => isRadio.value || isCheckbox.value)

const small = computed(() => ['small'].includes(_size.value as string))

const source = ref(0)
const _value = useTransition(source, {
  duration: props.animationDuration,
})
source.value = unref(props.value)

const displayValue = computed(() => {
  const { formatter, precision, decimalSeparator, groupSeparator } = props

  const value = props.animation ? _value.value : unref(props.value)

  if (isFunction(formatter)) return formatter(value)

  if (!isNumber(value)) return value

  let [integer, decimal = ''] = String(value).split('.')
  decimal = decimal
    .padEnd(precision, '0')
    .slice(0, precision > 0 ? precision : 0)
  integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
  return [integer, decimal].join(decimal ? decimalSeparator : '')
})

const wrapperStyle = computed(() => [
  props.style,
  {
    width: props.width ? addUnit(props.width) : '100%',
  },
])

const isVertical = computed(() => props.direction === 'vertical')

const modelValue = computed({
  get() {
    return isGroup.value ? statisticGroupContext!.modelValue : props.modelValue!
  },
  set(val: any) {
    if (isGroup.value) {
      statisticGroupContext!.changeEvent?.(val)
    } else {
      emit(UPDATE_MODEL_EVENT, val)
    }
  },
})

const isChecked = computed(() => {
  if (isCheckbox.value) {
    if (isArray(unref(modelValue.value))) {
      return (
        unref(modelValue.value) as unknown as Array<string | number>
      )?.includes(props.label as string)
    } else {
      return (unref(modelValue.value) as unknown as boolean) === true
    }
  }
  if (isRadio.value) {
    return isEqual(unref(modelValue.value), props.label)
  }
  return false
})

const handleClick = (e: MouseEvent) => {
  if (props.disabled) return
  if (isCheckbox.value) {
    if (isGroup.value) {
      if (isChecked.value) {
        modelValue.value = unref(
          modelValue.value as unknown as Array<string | number>
        )?.filter((_) => !isEqual(_, props.label))
      } else {
        modelValue.value = [
          ...unref(modelValue.value as unknown as Array<string | number>),
          props.label,
        ]
      }
    } else {
      modelValue.value = !isChecked.value
    }
  }
  if (isRadio.value) {
    modelValue.value = props.label
  }
  emit('click', e)
}

const handleCheckboxChange = (val: any) => {
  emit && emit(UPDATE_MODEL_EVENT, val)
}

defineExpose({
  /**
   * @description current display value
   */
  displayValue,
})
</script>
